import React, { useEffect, useMemo, useState, useRef } from "react";
import { Download, RefreshCw, Calendar, CalendarDays, Users, Mail, Lock, Search, Save, Globe, FileText, LogOut, Check, Layers, Send, MessageSquare, X, Trash2, RotateCcw, UserCheck, ClipboardList, UserPlus, FileDown, Receipt, Bell, CheckCircle2, ChevronLeft, ChevronRight } from "lucide-react";
import {
  getBookings, getApplications, getContacts, downloadCv,
  adminLogin, adminMe, getOverrides, putOverrides, submitIndexNow, updateStatus, sendBookingConfirmation, setArchived, approveApplication, deleteItem, getCleaners, approveCleaner, deleteCleaner, sendCleanerOnboarding, markBookingRead, markCleanerRead, setContractStatus, sendAhvReminders, unreadCount, getWorklogs, adminDeleteWorklog, inviteApplicant, exportWorklogs, getInvoiceSettings, saveInvoiceSettings, getInvoices, sendInvoice, invoicePdf, adjustInvoice, exportPayroll, sendLogReminders, unassignBooking, assignBooking, getSchedule, scheduleOpenPdf, adminBookingMessage, adminCleanerMessage, setToken, clearToken, getToken,
} from "../api";
import { translations } from "../i18n";
import { flattenStrings, getByPath, setByPath, deleteByPath } from "../overrides";
import { SEO_PAGES, getSeoDefault } from "../seoConfig";
import { CITY_FIELDS, cityDefaults, articleDefaults, CITY_LIST, ARTICLE_LIST } from "../pageContent";
import { LANDING_LIST, LANDING_FIELDS, landingEditableDefaults } from "../landingPages";
import { useLang } from "../context/LanguageContext";
import { InvoiceSendDialog } from "../components/InvoiceSendDialog";
import { AdminWorklogForm } from "../components/AdminWorklogForm";
import { WorklogRow } from "../components/WorklogRow";
import { MonthLock } from "../components/MonthLock";

const fmtDate = (s) => { try { return new Date(s).toLocaleString("de-CH"); } catch { return s; } };
const clone = (o) => JSON.parse(JSON.stringify(o || {}));
const COST_PER_HOUR = 30; // CHF Vollkosten Putzfrau/Std (Lohn inkl. Abgaben) – NUR intern
const laborCost = (hours) => (hours || 0) * COST_PER_HOUR;
const chf = (n) => `${Number(n).toFixed(2)} CHF`;

const monthLabelDe = (m) => { try { return new Date(m + "-01").toLocaleDateString("de-CH", { month: "long", year: "numeric" }); } catch { return m; } };
const currentMonth = () => new Date().toISOString().slice(0, 7);

const AdminChatBox = ({ messages, names, onSend, testid }) => {
  const [msgs, setMsgs] = useState(messages || []);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.scrollTop = ref.current.scrollHeight; }, [msgs.length]);
  const label = (s) => (s === "support" ? "Support (Sie)" : s === "customer" ? (names.customer || "Kunde") : (names.cleaner || "Putzfrau"));
  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    try { const r = await onSend(t); setMsgs(r.messages); setText(""); } catch (e) { alert("Fehler: " + (e.response?.data?.detail || e.message)); }
    setBusy(false);
  };
  return (
    <div className="max-w-2xl">
      <div ref={ref} data-testid={testid} className="space-y-2 max-h-64 overflow-y-auto mb-3 bg-white rounded-xl border border-slate-100 p-3">
        {msgs.length === 0 && <div className="text-sm text-slate-400 text-center py-3">Noch keine Nachrichten.</div>}
        {msgs.map((m) => (
          <div key={m.id} className={`flex ${m.sender === "support" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${m.sender === "support" ? "bg-teal-700 text-white" : m.sender === "cleaner" ? "bg-slate-100 text-slate-800" : "bg-white border border-slate-200 text-slate-800"}`}>
              <div className={`text-[10px] mb-0.5 ${m.sender === "support" ? "text-teal-100" : "text-slate-400"}`}>{label(m.sender)} · {fmtDate(m.created_at)}</div>
              {m.text}
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <input data-testid={`${testid}-input`} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }} placeholder="Als MyCleaner Support schreiben…" className="flex-1 h-10 rounded-lg border border-slate-200 px-3 text-sm" />
        <button data-testid={`${testid}-send`} disabled={busy || !text.trim()} onClick={send} className="inline-flex items-center gap-1.5 px-4 h-10 rounded-lg bg-teal-700 text-white text-sm font-semibold disabled:opacity-50"><Send className="w-4 h-4" /> Senden</button>
      </div>
    </div>
  );
};

const SETTING_FIELDS = [
  ["company_name", "Firmenname"], ["address", "Adresse (Strasse, PLZ, Ort)"], ["iban", "IBAN"],
  ["bank", "Bank"], ["vat_number", "MwSt-Nummer (optional)"], ["payment_terms_days", "Zahlungsfrist (Tage)"],
];

const InvoicesTab = () => {
  const [month, setMonth] = useState(currentMonth());
  const [invoices, setInvoices] = useState([]);
  const [settings, setSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [savedNote, setSavedNote] = useState(false);

  const load = async (m) => {
    setLoading(true);
    try { setInvoices(await getInvoices(m)); } catch (e) { setInvoices([]); }
    setLoading(false);
  };
  useEffect(() => { getInvoiceSettings().then(setSettings).catch(() => {}); }, []);
  useEffect(() => { load(month); }, [month]);

  const saveSettings = async () => {
    try { await saveInvoiceSettings(settings); setSavedNote(true); setTimeout(() => setSavedNote(false), 2000); }
    catch (e) { alert("Fehler beim Speichern"); }
  };
  const dl = async (inv) => { try { await invoicePdf(inv.booking_id, month, inv.invoice_no); } catch (e) { alert("Fehler: " + (e.response?.data?.detail || e.message)); } };
  const adjust = async (inv, val) => {
    const h = parseFloat(val);
    if (isNaN(h) || h === (inv.extra_hours || 0)) return;
    try { const d = await adjustInvoice(inv.booking_id, month, h); setInvoices((prev) => prev.map((x) => (x.booking_id === inv.booking_id ? { ...x, ...d } : x))); }
    catch (e) { alert("Fehler: " + (e.response?.data?.detail || e.message)); }
  };
  const [sendInv, setSendInv] = useState(null);
  const send = (inv) => setSendInv(inv);
  const doSend = async (message) => {
    const inv = sendInv;
    setSendingId(inv.booking_id);
    try { await sendInvoice(inv.booking_id, month, message); setSendInv(null); alert(`Rechnung an ${inv.email} gesendet.`); }
    catch (e) { alert("Fehler: " + (e.response?.data?.detail || e.message)); }
    setSendingId(null);
  };

  const totalSum = invoices.reduce((s, i) => s + (i.total || 0), 0);
  const noCompany = settings && !settings.address;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input data-testid="invoice-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-10 rounded-xl border border-slate-200 px-3 text-sm bg-white" />
        <button data-testid="invoice-settings-toggle" onClick={() => setShowSettings((v) => !v)} className="inline-flex items-center gap-2 px-4 h-10 rounded-xl bg-slate-100 text-slate-700 text-sm font-semibold hover:bg-slate-200">Rechnungs-Einstellungen</button>
        <span className="text-sm text-slate-400">Rechnungen: <strong className="text-slate-700">{invoices.length}</strong> · Total: <strong className="text-slate-700">{chf(totalSum)}</strong></span>
      </div>

      {noCompany && !showSettings && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-4 text-sm text-amber-800">
          Trage zuerst deine <button onClick={() => setShowSettings(true)} className="underline font-semibold">Firmen- & Zahlungsangaben</button> ein (Adresse, IBAN), damit die Rechnung vollständig ist.
        </div>
      )}

      {showSettings && settings && (
        <div className="bg-white rounded-2xl border border-slate-100 p-5 mb-5">
          <div className="text-sm font-semibold text-slate-700 mb-3">Rechnungs-Einstellungen (erscheinen auf jeder Rechnung)</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {SETTING_FIELDS.map(([key, label]) => (
              <div key={key} className={key === "address" ? "sm:col-span-2" : ""}>
                <label className="text-xs font-semibold text-slate-500">{label}</label>
                {key === "address"
                  ? <textarea data-testid={`inv-set-${key}`} value={settings[key] || ""} onChange={(e) => setSettings({ ...settings, [key]: e.target.value })} rows={2} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm mt-1" />
                  : <input data-testid={`inv-set-${key}`} value={settings[key] ?? ""} onChange={(e) => setSettings({ ...settings, [key]: key === "payment_terms_days" ? Number(e.target.value) || 0 : e.target.value })} className="w-full h-10 rounded-xl border border-slate-200 px-3 text-sm mt-1" />}
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3 mt-4">
            <button data-testid="inv-set-save" onClick={saveSettings} className="inline-flex items-center gap-2 px-5 h-10 rounded-xl bg-teal-700 text-white text-sm font-semibold hover:bg-teal-800"><Save className="w-4 h-4" /> Speichern</button>
            {savedNote && <span className="text-sm text-green-600 font-medium">Gespeichert ✓</span>}
            <span className="text-xs text-slate-400">MwSt lasse ich weg, solange keine MwSt-Nummer hinterlegt ist.</span>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-100 overflow-x-auto">
        <div className="px-4 py-3 border-b border-slate-100 text-sm text-slate-500">Rechnungen für {monthLabelDe(month)} – aus den erfassten Stunden (× 43.90 CHF). Neukunden-Erststunde wird automatisch als Rabatt abgezogen.</div>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-slate-500 border-b border-slate-100">{["Rechnungs-Nr.","Kunde","Stadt","Std.","Zusatz-Std.","Betrag","Rabatt","Total","Aktionen"].map(h => <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.booking_id} className="border-b border-slate-50 hover:bg-slate-50/60">
                <td className="px-4 py-3 font-mono text-xs text-slate-500 whitespace-nowrap">{inv.invoice_no}</td>
                <td className="px-4 py-3 font-medium text-slate-900">{inv.customer}<div className="text-xs text-slate-400">{inv.email}</div></td>
                <td className="px-4 py-3">{inv.city}</td>
                <td className="px-4 py-3">{inv.hours}{inv.extra_hours ? <span className="text-xs text-sky-600"> (inkl. +{inv.extra_hours})</span> : null}</td>
                <td className="px-4 py-3">
                  <input type="number" step="0.5" min="0" defaultValue={inv.extra_hours || 0} data-testid={`invoice-extra-${inv.booking_id}`}
                    onKeyDown={(e) => { if (e.key === "Enter") adjust(inv, e.target.value); }}
                    onBlur={(e) => adjust(inv, e.target.value)}
                    className="w-16 h-9 rounded-lg border border-slate-200 px-2 text-sm" title="Zusätzliche Stunden manuell hinzufügen (Enter zum Speichern)" />
                </td>
                <td className="px-4 py-3 text-slate-600">{chf(inv.subtotal)}</td>
                <td className="px-4 py-3 text-slate-500">{inv.free ? `-${chf(inv.free)}` : "–"}</td>
                <td className="px-4 py-3 font-bold text-teal-700">{chf(inv.total)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <button data-testid={`invoice-pdf-${inv.booking_id}`} onClick={() => dl(inv)} className="inline-flex items-center gap-1.5 text-slate-700 font-medium hover:underline whitespace-nowrap"><FileDown className="w-4 h-4" /> PDF</button>
                    <button data-testid={`invoice-send-${inv.booking_id}`} onClick={() => send(inv)} disabled={sendingId === inv.booking_id} className="inline-flex items-center gap-1.5 text-teal-700 font-medium hover:underline whitespace-nowrap disabled:opacity-50"><Send className="w-4 h-4" /> {sendingId === inv.booking_id ? "Sende…" : "Senden"}</button>
                  </div>
                </td>
              </tr>
            ))}
            {!loading && invoices.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">Keine erfassten Stunden in {monthLabelDe(month)}</td></tr>}
          </tbody>
        </table>
      </div>
      <InvoiceSendDialog inv={sendInv} sending={!!sendingId} onClose={() => setSendInv(null)} onSend={doSend} />
    </div>
  );
};

const STATUS_META = {
  red: { color: "bg-red-500", ring: "ring-red-400", label: "Kein Interesse" },
  orange: { color: "bg-amber-500", ring: "ring-amber-400", label: "Wartet auf Bestätigung" },
  green: { color: "bg-green-500", ring: "ring-green-400", label: "Hat Putzfrau" },
};

const StatusDots = ({ value, onChange, testidPrefix }) => (
  <div className="flex items-center gap-1.5">
    {["red", "orange", "green"].map((s) => {
      const active = value === s;
      const m = STATUS_META[s];
      return (
        <button
          key={s}
          data-testid={`${testidPrefix}-status-${s}`}
          title={m.label}
          onClick={() => onChange(active ? "new" : s)}
          className={`w-4 h-4 rounded-full transition-all ${m.color} ${active ? `ring-2 ring-offset-1 ${m.ring} scale-110` : "opacity-25 hover:opacity-70"}`}
        />
      );
    })}
  </div>
);

const MsgBtn = ({ target, onOpen }) => (
  <button
    data-testid={`msg-btn-${target.email}`}
    onClick={() => onOpen(target)}
    className="inline-flex items-center gap-1.5 text-teal-700 font-medium hover:underline whitespace-nowrap"
  >
    <MessageSquare className="w-4 h-4" /> Nachricht
  </button>
);

const WEEKDAYS_ADMIN = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const MONTHS_DE = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];
const startOfWeekMon = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const isoDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const isoWeekNo = (d) => { const x = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())); const day = (x.getUTCDay() + 6) % 7; x.setUTCDate(x.getUTCDate() - day + 3); const firstThu = new Date(Date.UTC(x.getUTCFullYear(), 0, 4)); const fday = (firstThu.getUTCDay() + 6) % 7; firstThu.setUTCDate(firstThu.getUTCDate() - fday + 3); return 1 + Math.round((x - firstThu) / (7 * 24 * 3600 * 1000)); };


const MessageModal = ({ target, onClose }) => {
  const [subject, setSubject] = useState("Ihre Anfrage bei MyCleaner.ch");
  const [body, setBody] = useState(`Hallo ${target.name || ""},\n\n\n\nFreundliche Grüsse\nMyCleaner.ch`);
  const openMail = () => {
    const href = `mailto:${target.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = href;
  };
  return (
    <div className="fixed inset-0 z-[200] bg-slate-900/50 flex items-center justify-center px-5" onClick={onClose}>
      <div data-testid="message-modal" className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-head font-bold text-lg text-slate-900">Nachricht schreiben</h3>
            <p className="text-sm text-slate-500 mt-0.5">An: <span className="font-medium text-slate-700">{target.name || target.email}</span> ({target.email})</p>
          </div>
          <button data-testid="message-close" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Betreff</label>
            <input data-testid="message-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full h-11 rounded-xl border border-slate-200 px-3.5 text-sm focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100" />
          </div>
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Nachricht</label>
            <textarea data-testid="message-body" rows={8} value={body} onChange={(e) => setBody(e.target.value)} className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100" />
          </div>
        </div>
        <p className="text-xs text-slate-400 mt-3">Öffnet Ihr E-Mail-Programm mit vorausgefüllter Nachricht. Sie senden dann mit einem Klick aus Ihrem eigenen Postfach.</p>
        <div className="flex items-center gap-3 mt-5">
          <button data-testid="message-open-mail" onClick={openMail} className="inline-flex items-center gap-2 bg-teal-700 text-white font-head font-bold px-6 py-3 rounded-xl hover:bg-teal-800 transition-colors"><Mail className="w-4 h-4" /> Im E-Mail-Programm öffnen</button>
          <button data-testid="message-cancel" onClick={onClose} className="text-sm text-slate-500 hover:text-slate-800">Abbrechen</button>
        </div>
      </div>
    </div>
  );
};

const TabBtn = ({ active, onClick, icon: Icon, label, count, testid, dot }) => (
  <button data-testid={testid} onClick={onClick} className={`relative inline-flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-colors ${active ? "bg-teal-700 text-white" : "bg-white text-slate-600 border border-slate-200 hover:border-teal-600"}`}>
    <Icon className="w-4 h-4" /> {label}{count != null && <span className={`ml-1 text-xs px-2 py-0.5 rounded-full ${active ? "bg-white/20" : "bg-slate-100"}`}>{count}</span>}
    {dot && <span data-testid={`${testid}-unread`} className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-red-500 ring-2 ring-white" />}
  </button>
);

const LangSwitch = ({ value, onChange, prefix }) => (
  <div className="inline-flex rounded-lg bg-slate-100 p-1">
    {["de", "en"].map((l) => (
      <button key={l} data-testid={`${prefix}-lang-${l}`} onClick={() => onChange(l)} className={`px-4 py-1.5 rounded-md text-sm font-semibold ${value === l ? "bg-white shadow text-teal-700" : "text-slate-500"}`}>
        {l.toUpperCase()}
      </button>
    ))}
  </div>
);

const Field = ({ label, hint, value, defaultValue, onChange, textarea, testid, rows }) => (
  <div>
    <div className="flex items-center justify-between mb-1.5">
      <label className="text-sm font-semibold text-slate-700">{label}</label>
      {hint && <span className="text-[11px] text-slate-400">{hint}</span>}
    </div>
    {textarea ? (
      <textarea data-testid={testid} rows={rows || 3} value={value} placeholder={defaultValue} onChange={(e) => onChange(e.target.value)} className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100" />
    ) : (
      <input data-testid={testid} value={value} placeholder={defaultValue} onChange={(e) => onChange(e.target.value)} className="w-full h-11 rounded-xl border border-slate-200 px-3.5 text-sm focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100" />
    )}
  </div>
);

const Admin = () => {
  const { reloadOverrides } = useLang();
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const [tab, setTab] = useState("bookings");
  const [onlyApproved, setOnlyApproved] = useState(false);
  const [openChat, setOpenChat] = useState(null);
  const [openCleanerChat, setOpenCleanerChat] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [apps, setApps] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [cleaners, setCleaners] = useState([]);
  const [worklogs, setWorklogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msgTarget, setMsgTarget] = useState(null);
  const [sendingId, setSendingId] = useState(null);

  const changeStatus = async (collection, id, status, setter) => {
    setter((prev) => prev.map((x) => (x.id === id ? { ...x, status } : x)));
    try { await updateStatus(collection, id, status); } catch (e) { loadData(); }
  };

  const sendConfirm = async (b) => {
    if (!window.confirm(`Automatische Bestätigung per E-Mail an ${b.email} senden?`)) return;
    setSendingId(b.id);
    try { await sendBookingConfirmation(b.id); alert(`Bestätigung an ${b.email} gesendet.`); }
    catch (e) { alert("Fehler: " + (e.response?.data?.detail || e.message)); }
    setSendingId(null);
  };

  const SETTERS = { bookings: setBookings, applications: setApps, contact: setContacts };
  const archiveItem = async (collection, id, archived) => {
    const setter = SETTERS[collection];
    setter((prev) => prev.map((x) => (x.id === id ? { ...x, archived } : x)));
    try { await setArchived(collection, id, archived); } catch (e) { loadData(); }
  };
  const removeItem = async (collection, id) => {
    if (!window.confirm("Diesen Eintrag ENDGÜLTIG löschen? Das kann nicht rückgängig gemacht werden.")) return;
    const setter = SETTERS[collection];
    setter((prev) => prev.filter((x) => x.id !== id));
    try { await deleteItem(collection, id); } catch (e) { loadData(); }
  };

  const toggleApprove = async (a) => {
    const next = !a.approved;
    setApps((prev) => prev.map((x) => (x.id === a.id ? { ...x, approved: next } : x)));
    try { await approveApplication(a.id, next); } catch (e) { loadData(); }
  };

  const toggleCleaner = async (c) => {
    const next = !c.approved;
    setCleaners((prev) => prev.map((x) => (x.id === c.id ? { ...x, approved: next } : x)));
    try { await approveCleaner(c.id, next); } catch (e) { loadData(); }
  };

  const removeCleaner = async (c) => {
    const activeJobs = bookings.filter((b) => b.assigned_cleaner_id === c.id && !b.archived).length;
    const extra = activeJobs > 0 ? `\n\nAchtung: ${activeJobs} aktive(r) Auftrag/Aufträge werden wieder in den Pool gestellt und sind für alle Putzfrauen sichtbar.` : "";
    if (!window.confirm(`Putzfrau "${c.name}" endgültig aus dem Portal löschen? Der Account wird entfernt und sie kann sich nicht mehr einloggen.${extra}`)) return;
    setCleaners((prev) => prev.filter((x) => x.id !== c.id));
    try { await deleteCleaner(c.id); loadData(); } catch (e) { loadData(); }
  };

  const [onboardingId, setOnboardingId] = useState(null);
  const sendOnboarding = async (c) => {
    if (!window.confirm(`Anleitung „So funktioniert MyCleaner" jetzt an ${c.name} (${c.email}) senden?`)) return;
    setOnboardingId(c.id);
    try { await sendCleanerOnboarding(c.id); window.alert(`Anleitung an ${c.email} gesendet.`); }
    catch (e) { window.alert("Senden fehlgeschlagen."); }
    setOnboardingId(null);
  };

  const openChatBooking = (b) => {
    const opening = openChat !== b.id;
    setOpenChat(opening ? b.id : null);
    if (opening && unreadCount(b.messages, b.read_at, "support") > 0) {
      setBookings((prev) => prev.map((x) => (x.id === b.id ? { ...x, read_at: { ...(x.read_at || {}), support: new Date().toISOString() } } : x)));
      markBookingRead(b.id).catch(() => {});
    }
  };
  const openChatCleaner = (c) => {
    const opening = openCleanerChat !== c.id;
    setOpenCleanerChat(opening ? c.id : null);
    if (opening && unreadCount(c.messages, c.read_at, "support") > 0) {
      setCleaners((prev) => prev.map((x) => (x.id === c.id ? { ...x, read_at: { ...(x.read_at || {}), support: new Date().toISOString() } } : x)));
      markCleanerRead(c.id).catch(() => {});
    }
  };
  const [ahvBusy, setAhvBusy] = useState(false);
  const sendAhvRemind = async () => {
    const missing = cleaners.filter((c) => c.approved && !(c.ahv_number || "").trim()).length;
    if (missing === 0) { window.alert("Alle freigegebenen Putzfrauen haben bereits eine AHV-Nummer hinterlegt."); return; }
    if (!window.confirm(`AHV-Erinnerung an ${missing} freigegebene Putzfrau(en) ohne AHV-Nummer senden?`)) return;
    setAhvBusy(true);
    try { const r = await sendAhvReminders(); window.alert(`AHV-Erinnerung an ${r.count} Putzfrau(en) gesendet.`); }
    catch (e) { window.alert("Senden fehlgeschlagen."); }
    setAhvBusy(false);
  };
  const toggleContract = async (c) => {
    const next = !c.contract_received;
    setCleaners((prev) => prev.map((x) => (x.id === c.id ? { ...x, contract_received: next } : x)));
    try { await setContractStatus(c.id, next); } catch (e) { loadData(); }
  };

  const [invitingId, setInvitingId] = useState(null);
  const inviteApp = async (a) => {
    if (!window.confirm(`${a.name} ins Portal einladen? Sie/er wird freigegeben und bekommt eine E-Mail mit einem Link zum Passwort festlegen.`)) return;
    setInvitingId(a.id);
    try {
      const res = await inviteApplicant(a.id);
      setApps((prev) => prev.map((x) => (x.id === a.id ? { ...x, approved: true, invited: true } : x)));
      const copy = window.confirm(`Einladung an ${res.email} gesendet.${res.existing ? " (Konto war bereits vorhanden – jetzt freigegeben.)" : ""}\n\nMöchtest du den Einladungs-Link kopieren (falls du ihn manuell weitergeben willst)?`);
      if (copy) { try { await navigator.clipboard.writeText(res.reset_url); } catch (e) { window.prompt("Einladungs-Link:", res.reset_url); } }
      loadData();
    } catch (e) { alert("Fehler: " + (e.response?.data?.detail || e.message)); }
    setInvitingId(null);
  };

  const [rapportMonth, setRapportMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const doExport = async (format) => {
    try { await exportWorklogs(format, rapportMonth); }
    catch (e) { alert("Export fehlgeschlagen: " + (e.response?.data?.detail || e.message)); }
  };
  const sendReminders = async () => {
    if (!window.confirm("Jetzt Erinnerungs-Mails an alle Putzfrauen mit fehlender Stundenerfassung senden?")) return;
    try { const r = await sendLogReminders(); alert(`${r.sent} Erinnerung(en) gesendet.`); }
    catch (e) { alert("Fehler: " + (e.response?.data?.detail || e.message)); }
  };
  const unassign = async (b) => {
    if (!window.confirm(`Zuweisung von ${b.assigned_cleaner_name} für "${b.name}" aufheben? Der Auftrag wird wieder für alle Putzfrauen sichtbar. ${b.assigned_cleaner_name} wird per E-Mail informiert.`)) return;
    try { await unassignBooking(b.id); await loadData(); }
    catch (e) { alert("Fehler: " + (e.response?.data?.detail || e.message)); }
  };

  const [assigningId, setAssigningId] = useState(null);
  const assignCleaner = async (b, cleanerId) => {
    if (!cleanerId) return;
    const c = cleaners.find((x) => x.id === cleanerId);
    if (!window.confirm(`"${b.name}" an ${c?.name} zuweisen? ${c?.name} und die Kundin erhalten eine E-Mail mit den Kontaktdaten.`)) return;
    setAssigningId(b.id);
    try { await assignBooking(b.id, cleanerId); await loadData(); }
    catch (e) { alert("Fehler: " + (e.response?.data?.detail || e.message)); }
    setAssigningId(null);
  };

  const [schedule, setSchedule] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [weekStart, setWeekStart] = useState(() => startOfWeekMon(new Date()));
  const loadSchedule = async () => {
    setScheduleLoading(true);
    try { setSchedule(await getSchedule()); } catch (e) { /* noop */ }
    setScheduleLoading(false);
  };
  useEffect(() => { if (tab === "schedule") loadSchedule(); /* eslint-disable-next-line */ }, [tab]);

  const assignFromPlan = async (job, cleanerId) => {
    if (!cleanerId) return;
    const c = schedule?.cleaners.find((x) => x.cleaner_id === cleanerId);
    if (!window.confirm(`"${job.customer}" an ${c?.name} zuweisen? ${c?.name} und die Kundin erhalten eine E-Mail mit den Kontaktdaten.`)) return;
    try { await assignBooking(job.booking_id, cleanerId); await loadSchedule(); await loadData(); }
    catch (e) { alert("Fehler: " + (e.response?.data?.detail || e.message)); }
  };

  const activeBookings = bookings.filter((b) => !b.archived);
  const activeApps = apps.filter((a) => !a.archived);
  const activeContacts = contacts.filter((c) => !c.archived);
  const bookingsUnread = activeBookings.some((b) => unreadCount(b.messages, b.read_at, "support") > 0);
  const cleanersUnread = cleaners.some((c) => unreadCount(c.messages, c.read_at, "support") > 0);
  const archivedItems = [
    ...bookings.filter((b) => b.archived).map((x) => ({ ...x, _coll: "bookings", _type: "Buchung", _sub: `${x.city} · ${x.hours} Std.` })),
    ...apps.filter((a) => a.archived).map((x) => ({ ...x, _coll: "applications", _type: "Bewerbung", _sub: x.city || "" })),
    ...contacts.filter((c) => c.archived).map((x) => ({ ...x, _coll: "contact", _type: "Kontakt", _sub: (x.message || "").slice(0, 40) })),
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  // overrides working copy { de:{...}, en:{...} }
  const [ov, setOv] = useState({ de: {}, en: {} });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!getToken()) { setChecking(false); return; }
    adminMe().then(() => { setAuthed(true); setChecking(false); }).catch(() => { clearToken(); setChecking(false); });
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [b, a, c, cl, wl] = await Promise.all([getBookings(), getApplications(), getContacts(), getCleaners().catch(() => []), getWorklogs().catch(() => [])]);
      setBookings(b); setApps(a); setContacts(c); setCleaners(cl); setWorklogs(wl);
    } catch (e) { /* noop */ }
    setLoading(false);
  };
  const loadOv = async () => { try { const o = await getOverrides(); setOv({ de: o.de || {}, en: o.en || {} }); } catch (e) { /* noop */ } };
  const reloadWorklogs = async () => { try { setWorklogs(await getWorklogs()); } catch (e) { /* noop */ } };
  const removeWorklog = async (w) => {
    if (!window.confirm(`Eintrag vom ${w.date} (${w.hours} Std., ${w.cleaner}) löschen?`)) return;
    try { await adminDeleteWorklog(w.booking_id, w.id); reloadWorklogs(); }
    catch (e) { alert("Fehler: " + (e.response?.data?.detail || e.message)); }
  };

  useEffect(() => { if (authed) { loadData(); loadOv(); } }, [authed]);

  const doLogin = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const res = await adminLogin(email.trim().toLowerCase(), pw);
      setToken(res.token); setAuthed(true);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Login fehlgeschlagen");
    }
    setBusy(false);
  };
  const doLogout = () => { clearToken(); setAuthed(false); setPw(""); };
  const [inBusy, setInBusy] = useState(false);
  const doIndexNow = async () => {
    setInBusy(true);
    try { const r = await submitIndexNow(); alert(`IndexNow: ${r.submitted} URLs an Bing/Yandex gemeldet (Status ${r.status_code}).`); }
    catch (e) { alert("IndexNow Fehler: " + (e.response?.data?.detail || e.message)); }
    setInBusy(false);
  };

  const saveLang = async (lang) => {
    setSaved(false);
    await putOverrides(lang, ov[lang] || {});
    await reloadOverrides();
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  if (checking) return <div className="min-h-screen bg-slate-50" />;

  if (!authed) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-5">
        <form onSubmit={doLogin} className="bg-white rounded-2xl border border-slate-100 shadow-lg p-8 w-full max-w-sm">
          <div className="w-12 h-12 rounded-xl bg-teal-700 flex items-center justify-center mb-5"><Lock className="w-6 h-6 text-white" /></div>
          <h1 className="font-head font-extrabold text-2xl text-slate-900 mb-1">Admin-Login</h1>
          <p className="text-slate-500 text-sm mb-6">Bitte mit E-Mail und Passwort anmelden.</p>
          <input data-testid="admin-email-input" type="email" value={email} onChange={(e) => { setEmail(e.target.value); setErr(""); }} placeholder="E-Mail" className="w-full h-12 rounded-xl border border-slate-200 px-4 mb-3 focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100" />
          <input data-testid="admin-password-input" type="password" value={pw} onChange={(e) => { setPw(e.target.value); setErr(""); }} placeholder="Passwort" className="w-full h-12 rounded-xl border border-slate-200 px-4 focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100" />
          {err && <p className="text-red-500 text-sm mt-2" data-testid="admin-login-error">{err}</p>}
          <button data-testid="admin-login-submit" type="submit" disabled={busy} className="w-full h-12 mt-5 rounded-xl bg-teal-700 text-white font-head font-bold hover:bg-teal-800 transition-colors disabled:opacity-60">{busy ? "…" : "Anmelden"}</button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pt-[100px] pb-16">
      <div className="max-w-[1200px] mx-auto px-5 md:px-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-head font-extrabold text-3xl text-slate-900">Admin</h1>
            <p className="text-slate-500 text-sm mt-1">Eingänge verwalten & Inhalte / SEO bearbeiten</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => { loadData(); loadOv(); }} className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium hover:border-teal-600"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Aktualisieren</button>
            <button data-testid="indexnow-btn" onClick={doIndexNow} disabled={inBusy} className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 border border-amber-200 rounded-xl px-4 py-2.5 text-sm font-medium hover:bg-amber-200 disabled:opacity-60"><Send className={`w-4 h-4 ${inBusy ? "animate-pulse" : ""}`} /> IndexNow</button>
            <button data-testid="admin-logout-btn" onClick={doLogout} className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium hover:border-red-400 hover:text-red-600"><LogOut className="w-4 h-4" /> Abmelden</button>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mb-6">
          <TabBtn testid="tab-bookings" active={tab === "bookings"} onClick={() => setTab("bookings")} icon={Calendar} label="Buchungen" count={activeBookings.length} dot={bookingsUnread} />
          <TabBtn testid="tab-schedule" active={tab === "schedule"} onClick={() => setTab("schedule")} icon={CalendarDays} label="Einsatzplan" />
          <TabBtn testid="tab-apps" active={tab === "apps"} onClick={() => setTab("apps")} icon={Users} label="Bewerbungen" count={activeApps.length} />
          <TabBtn testid="tab-contacts" active={tab === "contacts"} onClick={() => setTab("contacts")} icon={Mail} label="Kontakt" count={activeContacts.length} />
          <TabBtn testid="tab-seo" active={tab === "seo"} onClick={() => setTab("seo")} icon={Globe} label="SEO" />
          <TabBtn testid="tab-content" active={tab === "content"} onClick={() => setTab("content")} icon={Layers} label="Inhalte" />
          <TabBtn testid="tab-texts" active={tab === "texts"} onClick={() => setTab("texts")} icon={FileText} label="Texte" />
          <TabBtn testid="tab-trash" active={tab === "trash"} onClick={() => setTab("trash")} icon={Trash2} label="Gelöscht" count={archivedItems.length} />
          <TabBtn testid="tab-cleaners" active={tab === "cleaners"} onClick={() => setTab("cleaners")} icon={UserCheck} label="Putzfrauen" count={cleaners.length} dot={cleanersUnread} />
          <TabBtn testid="tab-rapport" active={tab === "rapport"} onClick={() => setTab("rapport")} icon={ClipboardList} label="Rapport" count={worklogs.length} />
          <TabBtn testid="tab-invoices" active={tab === "invoices"} onClick={() => setTab("invoices")} icon={Receipt} label="Rechnungen" />
        </div>

        {["bookings", "apps", "contacts"].includes(tab) && (
          <div data-testid="status-legend" className="flex flex-wrap items-center gap-4 mb-4 text-sm text-slate-600">
            <span className="font-semibold text-slate-500">Status:</span>
            {[["red", "Kein Interesse"], ["orange", "Wartet auf Bestätigung"], ["green", "Hat Putzfrau"]].map(([s, lbl]) => (
              <span key={s} className="inline-flex items-center gap-2">
                <span className={`w-3.5 h-3.5 rounded-full ${STATUS_META[s].color}`} /> {lbl}
              </span>
            ))}
            <span className="text-slate-400">· Punkt anklicken zum Setzen, erneut anklicken zum Zurücksetzen</span>
          </div>
        )}

        {tab === "schedule" && (
          <div data-testid="schedule-view">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
              <div className="text-sm text-slate-500">Automatischer Einsatzplan – jede zugewiesene/angenommene Putzfrau mit erstem Einsatztag erscheint hier. Wochentag = Tag des 1. Einsatzes.</div>
              <div className="flex items-center gap-2">
                <button data-testid="schedule-pdf" onClick={() => scheduleOpenPdf(isoDate(weekStart)).catch(() => {})} className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white font-semibold px-3 py-1.5 rounded-lg text-xs">
                  <FileDown className="w-3.5 h-3.5" /> Woche als PDF / Drucken
                </button>
                <button data-testid="schedule-refresh" onClick={loadSchedule} className="inline-flex items-center gap-1.5 bg-white border border-slate-200 hover:border-teal-600 text-slate-600 font-semibold px-3 py-1.5 rounded-lg text-xs">
                  <RefreshCw className="w-3.5 h-3.5" /> Aktualisieren
                </button>
              </div>
            </div>
            {scheduleLoading && <div className="text-slate-400 text-sm py-6">Lädt…</div>}
            {schedule && !scheduleLoading && (() => {
              const totalAssignedHours = schedule.cleaners.reduce((s, c) => s + (c.assigned_hours || 0), 0);
              const totalCapacity = schedule.cleaners.reduce((s, c) => s + (c.capacity_hours || 0), 0);
              const freeTotal = totalCapacity ? Math.round((totalCapacity - totalAssignedHours) * 10) / 10 : null;
              const overloadedCleaners = schedule.cleaners.filter((c) => c.capacity_hours && c.remaining_hours < 0);
              return (
                <div className="space-y-6">
                  {/* Summary */}
                  <div className="flex flex-wrap gap-3">
                    <div className="flex-1 min-w-[150px] bg-white rounded-xl border border-slate-100 px-4 py-3"><div className="text-xs text-slate-500">Aktive Putzfrauen</div><div className="font-head font-bold text-lg text-slate-900">{schedule.cleaners.length}</div></div>
                    <div className="flex-1 min-w-[150px] bg-white rounded-xl border border-slate-100 px-4 py-3"><div className="text-xs text-slate-500">Zugewiesene Std./Woche</div><div className="font-head font-bold text-lg text-slate-900">{Math.round(totalAssignedHours * 10) / 10} h</div></div>
                    <div className="flex-1 min-w-[150px] bg-white rounded-xl border border-teal-200 bg-teal-50/60 px-4 py-3"><div className="text-xs text-teal-700">Noch frei (Kapazität)</div><div className="font-head font-bold text-lg text-teal-700">{freeTotal != null ? `${freeTotal} h` : "—"}</div></div>
                    <div className="flex-1 min-w-[150px] bg-white rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-3"><div className="text-xs text-amber-700">Offene Aufträge (ohne Putzfrau)</div><div className="font-head font-bold text-lg text-amber-700">{schedule.open_jobs.length}</div></div>
                    <div data-testid="overload-summary" className={`flex-1 min-w-[150px] rounded-xl border px-4 py-3 ${overloadedCleaners.length ? "border-red-300 bg-red-50" : "bg-white border-slate-100"}`}><div className={`text-xs ${overloadedCleaners.length ? "text-red-700" : "text-slate-500"}`}>Überlastete Putzfrauen</div><div className={`font-head font-bold text-lg ${overloadedCleaners.length ? "text-red-600" : "text-slate-900"}`}>{overloadedCleaners.length}</div></div>
                  </div>

                  {/* Open jobs – assign directly from the plan */}
                  <div className="bg-white rounded-2xl border border-slate-100 overflow-x-auto" data-testid="open-jobs-panel">
                    <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700">Offene Aufträge – direkt zuweisen ({schedule.open_jobs.length})</div>
                    {schedule.open_jobs.length === 0 ? (
                      <div className="px-4 py-6 text-center text-slate-400 text-sm">Keine offenen Aufträge – alles zugewiesen 🎉</div>
                    ) : (
                      <table className="w-full text-sm">
                        <thead><tr className="text-left text-slate-500 border-b border-slate-100">{["Kunde","Ort","Std.","Häufigkeit","1. Einsatz","Zuweisen an"].map((h) => <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
                        <tbody>
                          {schedule.open_jobs.map((j) => (
                            <tr key={j.booking_id} className="border-b border-slate-50 hover:bg-slate-50/60">
                              <td className="px-4 py-3 font-medium text-slate-900">{j.customer}</td>
                              <td className="px-4 py-3 text-slate-600">{j.city}</td>
                              <td className="px-4 py-3">{j.hours}</td>
                              <td className="px-4 py-3 text-slate-500">{j.freq || "—"}</td>
                              <td className="px-4 py-3 text-slate-500">{j.first_visit_date || "—"}</td>
                              <td className="px-4 py-3">
                                <select data-testid={`plan-assign-${j.booking_id}`} value="" onChange={(e) => assignFromPlan(j, e.target.value)} className="text-xs rounded-lg border border-teal-300 bg-teal-50/50 text-teal-800 px-2 py-1.5 max-w-[200px] focus:outline-none focus:ring-2 focus:ring-teal-500">
                                  <option value="">+ Putzfrau wählen</option>
                                  {schedule.cleaners.map((c) => (
                                    <option key={c.cleaner_id} value={c.cleaner_id}>{c.name}{c.capacity_hours ? ` · ${c.remaining_hours > 0 ? `${c.remaining_hours}h frei` : "voll"}` : ""}</option>
                                  ))}
                                </select>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* Weekly calendar with real dates + navigation */}
                  {(() => {
                    const weekDates = WEEKDAYS_ADMIN.map((_, i) => addDays(weekStart, i));
                    const weekEnd = weekDates[6];
                    const todayIso = isoDate(new Date());
                    const jobsFor = (c, dayIdx) => {
                      const d = WEEKDAYS_ADMIN[dayIdx];
                      const diso = isoDate(weekDates[dayIdx]);
                      return c.assignments.filter((a) => a.weekday === d && (!a.first_visit_date || a.first_visit_date <= diso));
                    };
                    const label = weekStart.getMonth() === weekEnd.getMonth()
                      ? `${weekStart.getDate()}.–${weekEnd.getDate()}. ${MONTHS_DE[weekEnd.getMonth()]} ${weekEnd.getFullYear()}`
                      : `${weekStart.getDate()}. ${MONTHS_DE[weekStart.getMonth()]} – ${weekEnd.getDate()}. ${MONTHS_DE[weekEnd.getMonth()]} ${weekEnd.getFullYear()}`;
                    return (
                  <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                    <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
                      <div className="text-sm font-semibold text-slate-700">Wochen-Kalender</div>
                      <div className="flex items-center gap-2">
                        <button data-testid="week-prev" onClick={() => setWeekStart(addDays(weekStart, -7))} className="w-8 h-8 inline-flex items-center justify-center rounded-lg border border-slate-200 hover:border-teal-600 text-slate-600">‹</button>
                        <div data-testid="week-label" className="text-sm font-semibold text-slate-900 min-w-[190px] text-center">KW {isoWeekNo(weekStart)} · {label}</div>
                        <button data-testid="week-next" onClick={() => setWeekStart(addDays(weekStart, 7))} className="w-8 h-8 inline-flex items-center justify-center rounded-lg border border-slate-200 hover:border-teal-600 text-slate-600">›</button>
                        <button data-testid="week-today" onClick={() => setWeekStart(startOfWeekMon(new Date()))} className="px-3 h-8 inline-flex items-center rounded-lg border border-slate-200 hover:border-teal-600 text-slate-600 text-xs font-semibold">Heute</button>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="schedule-calendar">
                      <thead><tr className="text-left text-slate-500 border-b border-slate-100">
                        <th className="px-3 py-2 font-semibold sticky left-0 bg-white min-w-[160px]">Putzfrau</th>
                        {weekDates.map((dt, i) => {
                          const isToday = isoDate(dt) === todayIso;
                          return <th key={i} className={`px-3 py-2 font-semibold text-center min-w-[120px] ${isToday ? "bg-teal-50 text-teal-800" : ""}`}>{WEEKDAYS_ADMIN[i]}<div className="text-xs font-normal text-slate-400">{dt.getDate()}.{dt.getMonth() + 1}.</div></th>;
                        })}
                      </tr></thead>
                      <tbody>
                        {schedule.cleaners.map((c) => {
                          const overloaded = !!(c.capacity_hours && c.remaining_hours < 0);
                          const full = !!(c.capacity_hours && c.remaining_hours === 0);
                          return (
                          <tr key={c.cleaner_id} data-testid={`sched-row-${c.cleaner_id}`} className={`border-b border-slate-50 align-top ${overloaded ? "bg-red-50" : ""}`}>
                            <td className={`px-3 py-2 sticky left-0 ${overloaded ? "bg-red-50" : "bg-white"}`}>
                              <div className="font-medium text-slate-900 flex items-center gap-1.5">{c.name}{overloaded && <span data-testid={`overload-badge-${c.cleaner_id}`} className="text-[10px] font-bold text-white bg-red-500 px-1.5 py-0.5 rounded">ÜBERLASTET</span>}</div>
                              <div className="text-xs text-slate-400">{c.city}</div>
                              <div className="text-xs mt-0.5">{c.capacity_hours ? <span className={`font-semibold ${overloaded ? "text-red-600" : full ? "text-amber-600" : "text-teal-700"}`}>{c.assigned_hours}/{c.capacity_hours} h · {overloaded ? `${Math.abs(c.remaining_hours)} h zu viel` : full ? "voll" : `${c.remaining_hours} h frei`}</span> : <span className="text-slate-400">{c.assigned_hours} h · Kapazität offen</span>}</div>
                            </td>
                            {weekDates.map((dt, i) => {
                              const avail = (c.availability?.days || []).includes(WEEKDAYS_ADMIN[i]);
                              const jobs = jobsFor(c, i);
                              const isToday = isoDate(dt) === todayIso;
                              return (
                                <td key={i} className={`px-2 py-2 text-center ${isToday ? "bg-teal-50/50" : (avail && jobs.length === 0 ? "bg-teal-50/40" : "")}`}>
                                  {jobs.map((a) => (
                                    <div key={a.booking_id} data-testid={`cal-chip-${a.booking_id}`} className="mb-1 text-xs bg-teal-100 text-teal-900 rounded-lg px-2 py-1 text-left">
                                      <div className="font-semibold truncate">{a.customer}</div>
                                      <div className="text-teal-700">{a.city} · {a.hours} h</div>
                                      {a.shift_start && <div className="text-teal-600">{a.shift_start}{a.shift_end ? `–${a.shift_end}` : ""}</div>}
                                    </div>
                                  ))}
                                  {jobs.length === 0 && avail && <span className="text-[10px] text-teal-600 font-medium">frei</span>}
                                  {jobs.length === 0 && !avail && <span className="text-slate-200">·</span>}
                                </td>
                              );
                            })}
                          </tr>
                          );
                        })}
                        {schedule.cleaners.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">Noch keine freigegebenen Putzfrauen</td></tr>}
                      </tbody>
                    </table>
                    </div>
                  </div>
                    );
                  })()}

                  {/* Sortable-ish table (by cleaner) */}
                  <div className="bg-white rounded-2xl border border-slate-100 overflow-x-auto">
                    <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700">Alle Einsätze (Liste)</div>
                    <table className="w-full text-sm" data-testid="schedule-table">
                      <thead><tr className="text-left text-slate-500 border-b border-slate-100">{["Putzfrau","Kunde","Ort","Wochentag","Zeit","1. Einsatz","Std.","Häufigkeit","Status"].map((h) => <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
                      <tbody>
                        {schedule.cleaners.flatMap((c) => c.assignments.map((a) => (
                          <tr key={a.booking_id} className="border-b border-slate-50 hover:bg-slate-50/60">
                            <td className="px-4 py-3 font-medium text-slate-900">{c.name}</td>
                            <td className="px-4 py-3 text-slate-700">{a.customer}</td>
                            <td className="px-4 py-3 text-slate-600">{a.city}</td>
                            <td className="px-4 py-3">{a.weekday || <span className="text-amber-600">Termin offen</span>}</td>
                            <td className="px-4 py-3 text-slate-500">{a.shift_start ? `${a.shift_start}${a.shift_end ? `–${a.shift_end}` : ""}` : "—"}</td>
                            <td className="px-4 py-3 text-slate-500">{a.first_visit_date || "—"}</td>
                            <td className="px-4 py-3">{a.hours}</td>
                            <td className="px-4 py-3 text-slate-500">{a.freq || "—"}</td>
                            <td className="px-4 py-3"><span className={`inline-block w-3 h-3 rounded-full ${STATUS_META[a.status]?.color || "bg-slate-300"}`} /></td>
                          </tr>
                        )))}
                        {schedule.cleaners.every((c) => c.assignments.length === 0) && <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">Noch keine Einsätze zugewiesen</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}
          </div>
        )}


        {tab === "bookings" && (
          <div>
            {activeBookings.length > 0 && (() => {
              const regular = activeBookings.filter((b) => !b.neukunde);
              const revenue = regular.reduce((s, b) => s + (b.price_per_visit || 0), 0);
              const cost = regular.reduce((s, b) => s + laborCost(b.hours), 0);
              const profit = revenue - cost;
              const box = "flex-1 min-w-[150px] bg-white rounded-xl border border-slate-100 px-4 py-3";
              return (
                <div data-testid="booking-summary" className="flex flex-wrap gap-3 mb-4">
                  <div className={box}><div className="text-xs text-slate-500">Umsatz (reguläre Buchungen)</div><div className="font-head font-bold text-lg text-slate-900">{chf(revenue)}</div></div>
                  <div className={box}><div className="text-xs text-slate-500">Kosten Putzfrau ({COST_PER_HOUR}/Std.)</div><div className="font-head font-bold text-lg text-slate-700">{chf(cost)}</div></div>
                  <div className={`${box} ${profit < 0 ? "border-red-200 bg-red-50/60" : "border-teal-200 bg-teal-50/60"}`}><div className={`text-xs ${profit < 0 ? "text-red-700" : "text-teal-700"}`}>Gewinn (intern)</div><div className={`font-head font-bold text-lg ${profit < 0 ? "text-red-600" : "text-teal-700"}`}>{chf(profit)}</div></div>
                </div>
              );
            })()}
            <div className="text-xs text-slate-400 mb-2">Gewinn = Kundenpreis − {COST_PER_HOUR} CHF/Std. (Vollkosten Putzfrau, Lohn inkl. Abgaben). <strong>Neukunden-Erstbuchungen (1. Std. gratis) sind ausgenommen</strong> – einmalige Aktion. Nur intern sichtbar.</div>
            <div className="bg-white rounded-2xl border border-slate-100 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-500 border-b border-slate-100">{["Datum","Name","Kontakt","Stadt","Std.","Häufigkeit","Preis / Gewinn","Neukunde","Status","Nachricht"].map(h => <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody>
                {activeBookings.map((b) => (
                  <React.Fragment key={b.id}>
                  <tr className="border-b border-slate-50 hover:bg-slate-50/60">
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDate(b.created_at)}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">{b.name}
                      {b.cancelled && <div className="text-xs text-red-600 font-semibold mt-0.5">Von Kundin abgesagt</div>}
                    </td>
                    <td className="px-4 py-3 text-slate-600"><div>{b.email}</div><div className="text-xs text-slate-400">{b.phone}</div></td>
                    <td className="px-4 py-3">{b.city}
                      {b.pets && <div className="text-xs text-amber-600 font-medium">Haustiere</div>}
                      {b.laundry && <div className="text-xs text-sky-600 font-medium">Mithilfe Wäsche</div>}
                      {b.assigned_cleaner_name && <div className="text-xs text-green-700 font-medium mt-0.5">→ {b.assigned_cleaner_name}</div>}
                      {b.first_visit_date && <div className="text-xs text-slate-400">1. Einsatz: {b.first_visit_date}</div>}
                      {b.assigned_cleaner_name && !b.cancelled && (
                        <button data-testid={`unassign-${b.id}`} onClick={() => unassign(b)} className="inline-flex items-center gap-1 text-xs text-amber-700 font-medium hover:underline mt-1">
                          <RotateCcw className="w-3 h-3" /> Zuweisung aufheben
                        </button>
                      )}
                      {!b.assigned_cleaner_name && !b.cancelled && (
                        <select
                          data-testid={`assign-select-${b.id}`}
                          value=""
                          disabled={assigningId === b.id}
                          onChange={(e) => assignCleaner(b, e.target.value)}
                          className="mt-1.5 w-full max-w-[170px] text-xs rounded-lg border border-teal-300 bg-teal-50/50 text-teal-800 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-teal-500"
                        >
                          <option value="">{assigningId === b.id ? "Weise zu…" : "+ Putzfrau zuweisen"}</option>
                          {cleaners.filter((c) => c.approved).map((c) => (
                            <option key={c.id} value={c.id}>{c.name}{c.city ? ` · ${c.city}` : ""}</option>
                          ))}
                        </select>
                      )}
                    </td><td className="px-4 py-3">
                      <div>{b.hours}</div>
                      {(b.days?.length > 0 || b.time_window || b.start_date) && (
                        <div data-testid={`booking-wish-${b.id}`} className="text-[11px] text-slate-500 mt-1 leading-snug max-w-[160px]">
                          {b.days?.length > 0 && <div>{b.days.join(", ")}</div>}
                          {b.time_window && <div>{b.time_window}</div>}
                          {b.start_date && <div>ab {b.start_date.split("-").reverse().join(".")}</div>}
                        </div>
                      )}
                    </td><td className="px-4 py-3">{b.freq}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-700">{chf(b.price_per_visit)}</div>
                      {(() => { const g = (b.price_per_visit || 0) - laborCost(b.hours); return <div className={`text-xs font-bold mt-0.5 ${g < 0 ? "text-red-600" : "text-teal-700"}`}>Gewinn {chf(g)}</div>; })()}
                    </td>
                    <td className="px-4 py-3">{b.neukunde ? "Ja" : "Nein"}</td>
                    <td className="px-4 py-3"><StatusDots value={b.status} onChange={(s) => changeStatus("bookings", b.id, s, setBookings)} testidPrefix={`booking-${b.id}`} /></td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1.5 items-start">
                        <MsgBtn target={{ email: b.email, name: b.name }} onOpen={setMsgTarget} />
                        <button data-testid={`view-chat-${b.id}`} onClick={() => openChatBooking(b)} className="relative inline-flex items-center gap-1.5 text-teal-700 font-medium hover:underline whitespace-nowrap">
                          <MessageSquare className="w-4 h-4" /> Chat{(b.messages || []).length ? ` (${b.messages.length})` : ""}
                          {(() => { const u = unreadCount(b.messages, b.read_at, "support"); return u > 0 ? <span data-testid={`chat-unread-${b.id}`} className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">{u}</span> : null; })()}
                        </button>
                        <button data-testid={`send-confirm-${b.id}`} onClick={() => sendConfirm(b)} disabled={sendingId === b.id} className="inline-flex items-center gap-1.5 text-amber-700 font-medium hover:underline whitespace-nowrap disabled:opacity-50">
                          <Send className="w-4 h-4" /> {sendingId === b.id ? "Sende…" : "Bestätigung"}
                        </button>
                        <button data-testid={`archive-booking-${b.id}`} onClick={() => archiveItem("bookings", b.id, true)} className="inline-flex items-center gap-1.5 text-slate-400 font-medium hover:text-red-600 whitespace-nowrap">
                          <Trash2 className="w-4 h-4" /> Löschen
                        </button>
                      </div>
                    </td>
                  </tr>
                  {openChat === b.id && (
                    <tr data-testid={`chat-row-${b.id}`} className="bg-slate-50/70">
                      <td colSpan={10} className="px-4 py-4">
                        <div className="text-xs font-semibold text-slate-500 uppercase mb-2">Konversation mit {b.name}{b.assigned_cleaner_name ? ` & ${b.assigned_cleaner_name}` : ""} · als MyCleaner Support antworten</div>
                        <AdminChatBox
                          testid={`booking-chat-${b.id}`}
                          messages={b.messages || []}
                          names={{ customer: b.name, cleaner: b.assigned_cleaner_name }}
                          onSend={async (text) => {
                            const r = await adminBookingMessage(b.id, text);
                            setBookings((prev) => prev.map((x) => (x.id === b.id ? { ...x, messages: r.messages } : x)));
                            return r;
                          }}
                        />
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
                {activeBookings.length === 0 && <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-400">Noch keine Buchungen</td></tr>}
              </tbody>
            </table>
            </div>
          </div>
        )}

        {tab === "apps" && (
          <div>
            <div className="flex items-center gap-3 mb-4">
              <button data-testid="filter-approved-toggle" onClick={() => setOnlyApproved((v) => !v)} className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${onlyApproved ? "bg-green-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                <Check className="w-4 h-4" /> {onlyApproved ? "Nur Freigegebene" : "Alle anzeigen"}
              </button>
              <span className="text-sm text-slate-400">{activeApps.filter((a) => a.approved).length} freigegeben von {activeApps.length}</span>
            </div>
            <div className="bg-white rounded-2xl border border-slate-100 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-500 border-b border-slate-100">{["Datum","Name","Kontakt","Gebiet","Erfahrung","CV","Freigabe","Status","Nachricht"].map(h => <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody>
                {(onlyApproved ? activeApps.filter((a) => a.approved) : activeApps).map((a) => (
                  <tr key={a.id} className="border-b border-slate-50 hover:bg-slate-50/60">
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDate(a.created_at)}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">{a.name}</td>
                    <td className="px-4 py-3 text-slate-600"><div>{a.email}</div><div className="text-xs text-slate-400">{a.phone}</div></td>
                    <td className="px-4 py-3">{a.city}
                      {a.availability && <div className="text-xs text-teal-700 font-medium mt-0.5">{a.availability}</div>}
                    </td><td className="px-4 py-3 max-w-[280px] text-slate-600">{a.experience}</td>
                    <td className="px-4 py-3">{a.cv_filename ? <button data-testid={`cv-download-${a.id}`} onClick={() => downloadCv(a.id, a.cv_original)} className="inline-flex items-center gap-1.5 text-teal-700 font-medium hover:underline"><Download className="w-4 h-4" /> CV</button> : <span className="text-slate-300">–</span>}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1.5 items-start">
                        <button data-testid={`approve-app-${a.id}`} onClick={() => toggleApprove(a)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap ${a.approved ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                          <Check className="w-3.5 h-3.5" /> {a.approved ? "Freigegeben" : "Freigeben"}
                        </button>
                        <button data-testid={`invite-app-${a.id}`} onClick={() => inviteApp(a)} disabled={invitingId === a.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap bg-teal-700 text-white hover:bg-teal-800 disabled:opacity-50">
                          <UserPlus className="w-3.5 h-3.5" /> {invitingId === a.id ? "Sende…" : a.invited ? "Erneut einladen" : "Ins Portal einladen"}
                        </button>
                        {a.invited && <span className="text-[11px] text-teal-600 font-medium">✓ eingeladen</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3"><StatusDots value={a.status} onChange={(s) => changeStatus("applications", a.id, s, setApps)} testidPrefix={`app-${a.id}`} /></td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1.5 items-start">
                        <MsgBtn target={{ email: a.email, name: a.name }} onOpen={setMsgTarget} />
                        <button data-testid={`archive-app-${a.id}`} onClick={() => archiveItem("applications", a.id, true)} className="inline-flex items-center gap-1.5 text-slate-400 font-medium hover:text-red-600 whitespace-nowrap">
                          <Trash2 className="w-4 h-4" /> Löschen
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {(onlyApproved ? activeApps.filter((a) => a.approved).length === 0 : activeApps.length === 0) && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">{onlyApproved ? "Keine freigegebenen Bewerbungen" : "Noch keine Bewerbungen"}</td></tr>}
              </tbody>
            </table>
            </div>
          </div>
        )}

        {tab === "contacts" && (
          <div className="bg-white rounded-2xl border border-slate-100 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-500 border-b border-slate-100">{["Datum","Name","E-Mail","Nachricht","Status","Aktion"].map(h => <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody>
                {activeContacts.map((c) => (
                  <tr key={c.id} className="border-b border-slate-50 hover:bg-slate-50/60">
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDate(c.created_at)}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">{c.name}</td>
                    <td className="px-4 py-3 text-slate-600">{c.email}</td>
                    <td className="px-4 py-3 max-w-[420px] text-slate-600">{c.message}</td>
                    <td className="px-4 py-3"><StatusDots value={c.status} onChange={(s) => changeStatus("contact", c.id, s, setContacts)} testidPrefix={`contact-${c.id}`} /></td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1.5 items-start">
                        <MsgBtn target={{ email: c.email, name: c.name }} onOpen={setMsgTarget} />
                        <button data-testid={`archive-contact-${c.id}`} onClick={() => archiveItem("contact", c.id, true)} className="inline-flex items-center gap-1.5 text-slate-400 font-medium hover:text-red-600 whitespace-nowrap">
                          <Trash2 className="w-4 h-4" /> Löschen
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {activeContacts.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Noch keine Nachrichten</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "trash" && (
          <div className="bg-white rounded-2xl border border-slate-100 overflow-x-auto">
            <div className="px-4 py-3 border-b border-slate-100 text-sm text-slate-500">Gelöschte Einträge können wiederhergestellt oder endgültig entfernt werden.</div>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-500 border-b border-slate-100">{["Datum","Typ","Name","Kontakt","Details","Aktionen"].map(h => <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody>
                {archivedItems.map((it) => (
                  <tr key={`${it._coll}-${it.id}`} className="border-b border-slate-50 hover:bg-slate-50/60">
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDate(it.created_at)}</td>
                    <td className="px-4 py-3"><span className="inline-block px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold">{it._type}</span></td>
                    <td className="px-4 py-3 font-medium text-slate-900">{it.name}</td>
                    <td className="px-4 py-3 text-slate-600">{it.email}</td>
                    <td className="px-4 py-3 max-w-[280px] text-slate-500">{it._sub}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-4">
                        <button data-testid={`restore-${it.id}`} onClick={() => archiveItem(it._coll, it.id, false)} className="inline-flex items-center gap-1.5 text-teal-700 font-medium hover:underline whitespace-nowrap"><RotateCcw className="w-4 h-4" /> Wiederherstellen</button>
                        <button data-testid={`delete-${it.id}`} onClick={() => removeItem(it._coll, it.id)} className="inline-flex items-center gap-1.5 text-red-600 font-medium hover:underline whitespace-nowrap"><Trash2 className="w-4 h-4" /> Endgültig löschen</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {archivedItems.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Papierkorb ist leer</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "cleaners" && (
          <div className="bg-white rounded-2xl border border-slate-100 overflow-x-auto">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
              <span className="text-sm text-slate-500">Registrierte Putzfrauen. Nur <strong>freigegebene</strong> sehen offene Aufträge im Portal und können sie annehmen.</span>
              <button data-testid="send-ahv-reminders" onClick={sendAhvRemind} disabled={ahvBusy} className="inline-flex items-center gap-1.5 bg-amber-100 text-amber-800 hover:bg-amber-200 font-semibold px-3 py-1.5 rounded-lg text-xs disabled:opacity-50 whitespace-nowrap">
                <Bell className="w-3.5 h-3.5" /> {ahvBusy ? "Sendet…" : "AHV-Erinnerung senden"}
              </button>
            </div>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-500 border-b border-slate-100">{["Registriert","Name","E-Mail","Telefon","Gebiet","Aufträge","Nachricht","Freigabe","Vertrag","Löschen"].map(h => <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody>
                {cleaners.map((c) => {
                  const jobs = bookings.filter((b) => b.assigned_cleaner_id === c.id && !b.archived);
                  return (
                  <React.Fragment key={c.id}>
                  <tr className="border-b border-slate-50 hover:bg-slate-50/60">
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDate(c.created_at)}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">{c.name}{c.address ? <div className="text-xs font-normal text-slate-400 mt-0.5">{c.address}</div> : null}{c.ahv_number ? <div className="text-xs font-normal text-slate-400 mt-0.5">AHV: {c.ahv_number}</div> : (c.approved ? <div data-testid={`ahv-missing-${c.id}`} className="inline-flex items-center gap-1 text-[10px] font-semibold text-red-600 bg-red-50 border border-red-100 px-1.5 py-0.5 rounded mt-0.5">AHV fehlt</div> : null)}</td>
                    <td className="px-4 py-3 text-slate-600">{c.email}</td>
                    <td className="px-4 py-3 text-slate-600">{c.phone}</td>
                    <td className="px-4 py-3">{c.city}</td>
                    <td className="px-4 py-3">
                      {jobs.length === 0 ? <span className="text-slate-300">–</span> : (
                        <div className="space-y-0.5">
                          <div className="text-xs font-semibold text-green-700">{jobs.length} angenommen</div>
                          {jobs.map((b) => (
                            <div key={b.id} className="text-xs text-slate-500">{b.name} · {b.city}{b.first_visit_date ? ` · 1. Einsatz ${b.first_visit_date}` : " · Termin offen"}</div>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1.5 items-start">
                        <button data-testid={`view-cleaner-chat-${c.id}`} onClick={() => openChatCleaner(c)} className="relative inline-flex items-center gap-1.5 text-teal-700 font-medium hover:underline whitespace-nowrap">
                          <MessageSquare className="w-4 h-4" /> Chat{(c.messages || []).length ? ` (${c.messages.length})` : ""}
                          {(() => { const u = unreadCount(c.messages, c.read_at, "support"); return u > 0 ? <span data-testid={`cleaner-chat-unread-${c.id}`} className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">{u}</span> : null; })()}
                        </button>
                        <button data-testid={`send-onboarding-${c.id}`} onClick={() => sendOnboarding(c)} disabled={onboardingId === c.id} className="inline-flex items-center gap-1.5 text-slate-500 hover:text-teal-700 font-medium hover:underline whitespace-nowrap disabled:opacity-50">
                          <Send className="w-3.5 h-3.5" /> {onboardingId === c.id ? "Sendet…" : "Anleitung senden"}
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <button data-testid={`approve-cleaner-${c.id}`} onClick={() => toggleCleaner(c)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${c.approved ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                        <Check className="w-3.5 h-3.5" /> {c.approved ? "Freigegeben" : "Freigeben"}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1 items-start">
                        {c.contract_received ? (
                          <span data-testid={`contract-status-${c.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-green-700">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Erhalten{c.contract_received_at ? ` · ${new Date(c.contract_received_at).toLocaleDateString("de-CH")}` : ""}
                          </span>
                        ) : c.contract_downloaded_at ? (
                          <span data-testid={`contract-status-${c.id}`} className="inline-flex items-center gap-1 text-xs font-medium text-blue-600">
                            <Download className="w-3.5 h-3.5" /> Geladen · {new Date(c.contract_downloaded_at).toLocaleDateString("de-CH")}
                          </span>
                        ) : (
                          <span data-testid={`contract-status-${c.id}`} className="text-xs text-slate-400">Noch nicht geladen</span>
                        )}
                        <button data-testid={`toggle-contract-${c.id}`} onClick={() => toggleContract(c)} className={`text-xs font-medium hover:underline ${c.contract_received ? "text-slate-400" : "text-teal-700"}`}>
                          {c.contract_received ? "Zurücksetzen" : "Als erhalten markieren"}
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <button data-testid={`delete-cleaner-${c.id}`} onClick={() => removeCleaner(c)} className="inline-flex items-center gap-1.5 text-red-600 font-medium hover:underline whitespace-nowrap">
                        <Trash2 className="w-4 h-4" /> Löschen
                      </button>
                    </td>
                  </tr>
                  {openCleanerChat === c.id && (
                    <tr data-testid={`cleaner-chat-row-${c.id}`} className="bg-slate-50/70">
                      <td colSpan={10} className="px-4 py-4">
                        <div className="text-xs font-semibold text-slate-500 uppercase mb-2">Nachrichten mit {c.name} · als MyCleaner Support schreiben</div>
                        <AdminChatBox
                          testid={`cleaner-chat-${c.id}`}
                          messages={c.messages || []}
                          names={{ cleaner: c.name }}
                          onSend={async (text) => {
                            const r = await adminCleanerMessage(c.id, text);
                            setCleaners((prev) => prev.map((x) => (x.id === c.id ? { ...x, messages: r.messages } : x)));
                            return r;
                          }}
                        />
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                  );
                })}
                {cleaners.length === 0 && <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-400">Noch keine registrierten Putzfrauen</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "rapport" && (() => {
          const months = Array.from(new Set(worklogs.map((w) => (w.date || "").slice(0, 7)).filter(Boolean))).sort().reverse();
          const shown = worklogs.filter((w) => (w.date || "").startsWith(rapportMonth));
          const shiftMonth = (d) => { const [y, m] = rapportMonth.split("-").map(Number); const n = new Date(Date.UTC(y, m - 1 + d, 1)); setRapportMonth(n.toISOString().slice(0, 7)); };
          const byCleaner = {};
          shown.forEach((w) => { const k = w.cleaner || "—"; byCleaner[k] = (byCleaner[k] || 0) + (Number(w.hours) || 0); });
          const grand = shown.reduce((s, w) => s + (Number(w.hours) || 0), 0);
          const monthLabel = (m) => { try { return new Date(m + "-01").toLocaleDateString("de-CH", { month: "long", year: "numeric" }); } catch { return m; } };
          return (
            <div>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="inline-flex items-center gap-1 bg-white rounded-xl border border-slate-200 p-1">
                  <button data-testid="rapport-prev-month" onClick={() => shiftMonth(-1)} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center" title="Vormonat"><ChevronLeft className="w-4 h-4" /></button>
                  <span data-testid="rapport-month-label" className="px-3 font-head font-semibold text-slate-900 min-w-[140px] text-center capitalize">{monthLabel(rapportMonth)}</span>
                  <button data-testid="rapport-next-month" onClick={() => shiftMonth(1)} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center" title="Nächster Monat"><ChevronRight className="w-4 h-4" /></button>
                </div>
                <select data-testid="rapport-month" value={months.includes(rapportMonth) ? rapportMonth : ""} onChange={(e) => e.target.value && setRapportMonth(e.target.value)} className="h-10 rounded-xl border border-slate-200 px-3 text-sm bg-white">
                  <option value="">Monat mit Einträgen…</option>
                  {months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
                </select>
                <MonthLock month={rapportMonth} label={monthLabel(rapportMonth)} />
                <button data-testid="rapport-export-csv" onClick={() => doExport("csv")} className="inline-flex items-center gap-2 px-4 h-10 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"><FileDown className="w-4 h-4" /> CSV (Excel)</button>
                <button data-testid="rapport-export-pdf" onClick={() => doExport("pdf")} className="inline-flex items-center gap-2 px-4 h-10 rounded-xl bg-teal-700 text-white text-sm font-semibold hover:bg-teal-800"><FileDown className="w-4 h-4" /> PDF</button>
                <span className="text-sm text-slate-400">Gesamt: <strong className="text-slate-700">{grand} Std.</strong></span>
              </div>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="text-sm font-semibold text-slate-600">Lohnübersicht (Treuhänder, à 27 CHF):</span>
                <button data-testid="payroll-export-csv" onClick={() => exportPayroll("csv", rapportMonth).catch((e) => alert("Fehler: " + (e.response?.data?.detail || e.message)))} className="inline-flex items-center gap-2 px-4 h-9 rounded-xl bg-slate-100 text-slate-700 text-sm font-semibold hover:bg-slate-200"><FileDown className="w-4 h-4" /> Lohn CSV</button>
                <button data-testid="payroll-export-pdf" onClick={() => exportPayroll("pdf", rapportMonth).catch((e) => alert("Fehler: " + (e.response?.data?.detail || e.message)))} className="inline-flex items-center gap-2 px-4 h-9 rounded-xl bg-slate-100 text-slate-700 text-sm font-semibold hover:bg-slate-200"><FileDown className="w-4 h-4" /> Lohn PDF</button>
                <button data-testid="send-reminders" onClick={sendReminders} className="inline-flex items-center gap-2 px-4 h-9 rounded-xl bg-amber-100 text-amber-800 text-sm font-semibold hover:bg-amber-200"><Bell className="w-4 h-4" /> Erinnerungen senden</button>
                <span className="text-xs text-slate-400">Automatisch jeden Sonntag 18:00 an Putzfrauen ohne aktuelle Stundenerfassung.</span>
              </div>
              <AdminWorklogForm bookings={bookings} onSaved={reloadWorklogs} />
              <div className="flex flex-wrap gap-3 mb-4">
                {Object.entries(byCleaner).map(([name, h]) => (
                  <div key={name} className="bg-white rounded-xl border border-slate-100 px-4 py-3 min-w-[150px]"><div className="text-xs text-slate-500">{name}</div><div className="font-head font-bold text-lg text-slate-900">{h} Std.</div></div>
                ))}
              </div>
              <div className="bg-white rounded-2xl border border-slate-100 overflow-x-auto">
                <div className="px-4 py-3 border-b border-slate-100 text-sm text-slate-500">Stunden-Rapport (wer / wo / wann / wie viele Stunden) – exportiere als CSV für den Treuhänder oder als PDF für die Kundenrechnung.</div>
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-slate-500 border-b border-slate-100">{["Datum","Putzfrau","Kunde","Stadt","Ort/Adresse","Stunden","Notiz",""].map(h => <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
                  <tbody>
                    {shown.map((w, i) => <WorklogRow key={w.id || i} w={w} onDelete={removeWorklog} onSaved={reloadWorklogs} />)}
                    {shown.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">Noch keine erfassten Stunden im {monthLabel(rapportMonth)}</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

        {tab === "invoices" && <InvoicesTab />}

        {tab === "seo" && <SeoEditor ov={ov} setOv={setOv} onSave={saveLang} saved={saved} />}
        {tab === "content" && <ContentEditor ov={ov} setOv={setOv} onSave={saveLang} saved={saved} />}
        {tab === "texts" && <TextEditor ov={ov} setOv={setOv} onSave={saveLang} saved={saved} />}
      </div>
      {msgTarget && <MessageModal target={msgTarget} onClose={() => setMsgTarget(null)} />}
    </div>
  );
};

const SavedPill = ({ saved }) => saved ? (
  <span className="inline-flex items-center gap-1.5 text-green-600 text-sm font-semibold"><Check className="w-4 h-4" /> Gespeichert</span>
) : null;

const SeoEditor = ({ ov, setOv, onSave, saved }) => {
  const [lang, setLang] = useState("de");
  const [pageKey, setPageKey] = useState("home");
  const [q, setQ] = useState("");

  const def = getSeoDefault(pageKey, lang);
  const base = `_seo.${pageKey}`;
  const val = (field) => { const v = getByPath(ov[lang], `${base}.${field}`); return v == null ? "" : v; };
  const set = (field, value) => {
    setOv((prev) => {
      const next = { ...prev, [lang]: clone(prev[lang]) };
      setByPath(next[lang], `${base}.${field}`, value);
      return next;
    });
  };
  const reset = () => {
    setOv((prev) => {
      const next = { ...prev, [lang]: clone(prev[lang]) };
      deleteByPath(next[lang], base);
      return next;
    });
  };

  const pages = SEO_PAGES.filter((p) => p.label.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="grid lg:grid-cols-[300px_1fr] gap-6">
      <div className="bg-white rounded-2xl border border-slate-100 p-4">
        <div className="relative mb-3">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="seo-page-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Seite suchen…" className="w-full h-10 rounded-lg border border-slate-200 pl-9 pr-3 text-sm focus:outline-none focus:border-teal-600" />
        </div>
        <div className="max-h-[520px] overflow-y-auto pr-1">
          {pages.map((p) => (
            <button key={p.key} data-testid={`seo-page-${p.key}`} onClick={() => setPageKey(p.key)} className={`w-full text-left px-3 py-2 rounded-lg text-sm mb-0.5 ${pageKey === p.key ? "bg-teal-50 text-teal-700 font-semibold" : "text-slate-600 hover:bg-slate-50"}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="font-head font-bold text-lg text-slate-900">{SEO_PAGES.find((p) => p.key === pageKey)?.label}</h2>
            <p className="text-xs text-slate-400 mt-0.5">Leer lassen = Standardtext wird verwendet</p>
          </div>
          <LangSwitch value={lang} onChange={setLang} prefix="seo" />
        </div>
        <div className="space-y-4">
          <Field testid="seo-title-input" label="Meta-Titel (SEO-Titel)" hint={`${(val("title") || def.title || "").length} Zeichen`} value={val("title")} defaultValue={def.title} onChange={(v) => set("title", v)} />
          <Field testid="seo-description-input" label="Meta-Beschreibung" hint={`${(val("description") || def.description || "").length} Zeichen`} value={val("description")} defaultValue={def.description} onChange={(v) => set("description", v)} textarea />
          <Field testid="seo-keywords-input" label="Keywords" value={val("keywords")} defaultValue={def.keywords} onChange={(v) => set("keywords", v)} textarea />
        </div>
        <div className="flex items-center gap-3 mt-6">
          <button data-testid="seo-save-btn" onClick={() => onSave(lang)} className="inline-flex items-center gap-2 bg-teal-700 text-white font-head font-bold px-6 py-3 rounded-xl hover:bg-teal-800 transition-colors"><Save className="w-4 h-4" /> Speichern ({lang.toUpperCase()})</button>
          <button data-testid="seo-reset-btn" onClick={reset} className="text-sm text-slate-500 hover:text-red-600">Auf Standard zurücksetzen</button>
          <SavedPill saved={saved} />
        </div>
      </div>
    </div>
  );
};

const TextEditor = ({ ov, setOv, onSave, saved }) => {
  const [lang, setLang] = useState("de");
  const sections = Object.keys(translations[lang]);
  const [section, setSection] = useState("hero");
  const [q, setQ] = useState("");

  const base = translations[lang][section];
  const leaves = useMemo(() => {
    if (typeof base === "string") return [{ path: section, value: base }];
    return flattenStrings(base, section);
  }, [base, section]);

  const filtered = leaves.filter((l) => l.value.toLowerCase().includes(q.toLowerCase()) || l.path.toLowerCase().includes(q.toLowerCase()));

  const val = (path) => { const v = getByPath(ov[lang], path); return v == null ? "" : v; };
  const set = (path, value) => {
    setOv((prev) => {
      const next = { ...prev, [lang]: clone(prev[lang]) };
      setByPath(next[lang], path, value);
      return next;
    });
  };

  return (
    <div className="grid lg:grid-cols-[240px_1fr] gap-6">
      <div className="bg-white rounded-2xl border border-slate-100 p-4">
        <LangSwitch value={lang} onChange={setLang} prefix="text" />
        <div className="mt-4 max-h-[520px] overflow-y-auto pr-1">
          {sections.map((s) => (
            <button key={s} data-testid={`text-section-${s}`} onClick={() => setSection(s)} className={`w-full text-left px-3 py-2 rounded-lg text-sm mb-0.5 capitalize ${section === s ? "bg-teal-50 text-teal-700 font-semibold" : "text-slate-600 hover:bg-slate-50"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6">
        <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
          <h2 className="font-head font-bold text-lg text-slate-900 capitalize">{section}</h2>
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="text-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Text suchen…" className="h-10 rounded-lg border border-slate-200 pl-9 pr-3 text-sm focus:outline-none focus:border-teal-600" />
          </div>
        </div>
        <p className="text-xs text-slate-400 mb-4">Leer lassen = Originaltext bleibt. Vorlagen mit Variablen (z.B. Städtenamen) sind hier nicht aufgeführt.</p>
        <div className="space-y-4 max-h-[520px] overflow-y-auto pr-1">
          {filtered.map((l) => (
            <Field key={l.path} testid={`text-field-${l.path}`} label={l.path.replace(`${section}.`, "")} value={val(l.path)} defaultValue={l.value} onChange={(v) => set(l.path, v)} textarea={l.value.length > 60} />
          ))}
          {filtered.length === 0 && <p className="text-slate-400 text-sm">Keine bearbeitbaren Texte gefunden.</p>}
        </div>
        <div className="flex items-center gap-3 mt-6 border-t border-slate-100 pt-5">
          <button data-testid="text-save-btn" onClick={() => onSave(lang)} className="inline-flex items-center gap-2 bg-teal-700 text-white font-head font-bold px-6 py-3 rounded-xl hover:bg-teal-800 transition-colors"><Save className="w-4 h-4" /> Speichern ({lang.toUpperCase()})</button>
          <SavedPill saved={saved} />
        </div>
      </div>
    </div>
  );
};

const ContentEditor = ({ ov, setOv, onSave, saved }) => {
  const [lang, setLang] = useState("de");
  const [type, setType] = useState("city");
  const [slug, setSlug] = useState(CITY_LIST[0].slug);
  const [q, setQ] = useState("");

  const listFor = (tp) => (tp === "city" ? CITY_LIST : tp === "article" ? ARTICLE_LIST : LANDING_LIST);
  const labelOf = (tp, x) => (tp === "city" ? x.name : tp === "article" ? x.title : x.label);
  const list = listFor(type);
  const filtered = list.filter((x) => (labelOf(type, x) || "").toLowerCase().includes(q.toLowerCase()));
  const key = `${type}:${slug}`;
  const base = `_pages.${key}`;
  const defs = type === "city" ? cityDefaults(slug, lang) : type === "article" ? articleDefaults(slug) : landingEditableDefaults(slug, lang);

  const val = (fk) => { const v = getByPath(ov[lang], `${base}.${fk}`); return v == null ? "" : v; };
  const set = (fk, value) => setOv((prev) => { const next = { ...prev, [lang]: clone(prev[lang]) }; setByPath(next[lang], `${base}.${fk}`, value); return next; });
  const reset = () => setOv((prev) => { const next = { ...prev, [lang]: clone(prev[lang]) }; deleteByPath(next[lang], base); return next; });

  const switchType = (tp) => { setType(tp); setSlug(listFor(tp)[0].slug); setQ(""); };

  const fields = type === "city"
    ? CITY_FIELDS
    : type === "landing"
    ? LANDING_FIELDS
    : [
        { key: "title", label: "Titel (H1)", textarea: false },
        { key: "excerpt", label: "Kurzbeschreibung (Teaser)", textarea: true },
        { key: "body", label: "Inhalt (## = Zwischentitel, - = Aufzählungspunkt, Leerzeile = neuer Absatz)", textarea: true, big: true },
      ];

  const itemLabel = labelOf(type, list.find((x) => x.slug === slug) || {});

  return (
    <div className="grid lg:grid-cols-[300px_1fr] gap-6">
      <div className="bg-white rounded-2xl border border-slate-100 p-4">
        <div className="inline-flex rounded-lg bg-slate-100 p-1 mb-3 w-full">
          {[["city", "Städte"], ["landing", "Landing"], ["article", "Ratgeber"]].map(([tp, lbl]) => (
            <button key={tp} data-testid={`content-type-${tp}`} onClick={() => switchType(tp)} className={`flex-1 px-2.5 py-1.5 rounded-md text-sm font-semibold ${type === tp ? "bg-white shadow text-teal-700" : "text-slate-500"}`}>{lbl}</button>
          ))}
        </div>
        <div className="relative mb-3">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="content-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suchen…" className="w-full h-10 rounded-lg border border-slate-200 pl-9 pr-3 text-sm focus:outline-none focus:border-teal-600" />
        </div>
        <div className="max-h-[520px] overflow-y-auto pr-1">
          {filtered.map((x) => (
            <button key={x.slug} data-testid={`content-item-${x.slug}`} onClick={() => setSlug(x.slug)} className={`w-full text-left px-3 py-2 rounded-lg text-sm mb-0.5 ${slug === x.slug ? "bg-teal-50 text-teal-700 font-semibold" : "text-slate-600 hover:bg-slate-50"}`}>
              {labelOf(type, x)}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6">
        <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
          <div>
            <h2 className="font-head font-bold text-lg text-slate-900">{itemLabel}</h2>
            <p className="text-xs text-slate-400 mt-0.5">Leer lassen = Originaltext bleibt. DE und EN werden getrennt gespeichert.</p>
          </div>
          <LangSwitch value={lang} onChange={setLang} prefix="content" />
        </div>
        <div className="space-y-4 max-h-[520px] overflow-y-auto pr-1">
          {fields.map((fld) => (
            <Field key={fld.key} testid={`content-field-${fld.key}`} label={fld.label} value={val(fld.key)} defaultValue={defs[fld.key]} onChange={(v) => set(fld.key, v)} textarea={fld.textarea} rows={fld.big ? 16 : 3} />
          ))}
        </div>
        <div className="flex items-center gap-3 mt-6 border-t border-slate-100 pt-5">
          <button data-testid="content-save-btn" onClick={() => onSave(lang)} className="inline-flex items-center gap-2 bg-teal-700 text-white font-head font-bold px-6 py-3 rounded-xl hover:bg-teal-800 transition-colors"><Save className="w-4 h-4" /> Speichern ({lang.toUpperCase()})</button>
          <button data-testid="content-reset-btn" onClick={reset} className="text-sm text-slate-500 hover:text-red-600">Auf Standard zurücksetzen</button>
          <SavedPill saved={saved} />
        </div>
      </div>
    </div>
  );
};

export default Admin;
